import {
   createEntityAdapter,
   createSelector,
   createSlice,
   PayloadAction,
} from "@reduxjs/toolkit"
import {
   removeWearable,
   selectWearableRealtimeById,
} from "./wearablesRealtimeSlice"
import {
   ProducerType,
   type RealtimeProducer,
   type WsFeedPayload,
   type WsInmateSummary,
} from "@/hooks/useRealtimeConnection"
import type { RealtimeFeedPayload } from "@/hooks/useRealtimeConnection"
import type { AppDispatch, RootState } from "@/store"
import { normalizeMacAddress } from "@/utils/index"

export interface UnknownLocationWearableEntry {
   id: string
   macAddress: string
   inmate: {
      firstName?: string
      middleName?: string | null
      lastName?: string
      customIdentifier?: string
      id?: string
      externalId?: string
      bookingNumber?: string | null
   } | null
   unknownSince: number
}

const adapter = createEntityAdapter<UnknownLocationWearableEntry>()

export const initialState = adapter.getInitialState()

const unknownLocationWearablesSlice = createSlice({
   name: "unknownLocationWearables",
   initialState,
   reducers: {
      wearableLocationUnknown: (
         state,
         action: PayloadAction<{
            id: string
            macAddress: string
            inmate: UnknownLocationWearableEntry["inmate"]
            timestamp: number
         }>
      ) => {
         const { id, macAddress, inmate, timestamp } = action.payload
         adapter.upsertOne(state, {
            id,
            macAddress,
            inmate,
            unknownSince: timestamp,
         })
      },
      wearableRemovedFromUnknownLocation: (
         state,
         action: PayloadAction<string>
      ) => {
         adapter.removeOne(state, action.payload)
      },
      resetUnknownLocationWearables: () => initialState,
   },
})

export const {
   wearableLocationUnknown,
   wearableRemovedFromUnknownLocation,
   resetUnknownLocationWearables,
} = unknownLocationWearablesSlice.actions

const adapterSelectors = adapter.getSelectors(
   (state: RootState) => state.unknownLocationWearablesReducer
)

export const selectUnknownLocationWearableIds = adapterSelectors.selectIds as (
   state: RootState
) => string[]

export const selectUnknownLocationWearableById = (
   state: RootState,
   wearableId: string
) => adapterSelectors.selectById(state, wearableId)

export const selectUnknownLocationProducers = createSelector(
   adapterSelectors.selectAll,
   (entries): RealtimeProducer[] =>
      entries.map((entry) => ({
         entityId: entry.id,
         macAddress: entry.macAddress,
         customIdentifier: "",
      }))
)

export const hideRemovedWearable =
   (entityId: string) => (dispatch: AppDispatch, getState: () => RootState) => {
      const state = getState()
      if (selectWearableRealtimeById(state, entityId)) {
         dispatch(removeWearable(entityId))
      }
      if (selectUnknownLocationWearableIds(state).includes(entityId)) {
         dispatch(wearableRemovedFromUnknownLocation(entityId))
      }
   }

export const assignWearableToUnknownLocation =
   ({
      macAddress,
      inmate,
   }: {
      macAddress: string
      inmate?: WsInmateSummary | null
   }) =>
   (dispatch: AppDispatch) => {
      const wearableId = normalizeMacAddress(macAddress)
      dispatch(
         wearableLocationUnknown({
            id: wearableId,
            macAddress,
            inmate: inmate
               ? {
                    id: inmate.id,
                    externalId: inmate.external_id ?? undefined,
                    customIdentifier: inmate.custom_identifier,
                    firstName: inmate.first_name,
                    middleName: inmate.middle_name,
                    lastName: inmate.last_name,
                    bookingNumber: inmate.booking_number,
                 }
               : null,
            timestamp: Date.now(),
         })
      )
   }

export const resolveWearableLocations =
   (feeds: RealtimeFeedPayload[]) =>
   (dispatch: AppDispatch, getState: () => RootState) => {
      const state = getState()
      const unknownIds = selectUnknownLocationWearableIds(state)

      feeds.forEach((feed) => {
         const payload = feed.rawPayload as WsFeedPayload
         if (payload.producer_type !== ProducerType.WEARABLE_POSITIONS) return
         if (!unknownIds.includes(feed.entityId)) return
         dispatch(wearableRemovedFromUnknownLocation(feed.entityId))
      })
   }

export default unknownLocationWearablesSlice.reducer
